import { useState } from "react";

const sections = [
  { id: "overview", label: "Overview" },
  { id: "diagnose", label: "Phase 1 — Diagnose" },
  { id: "design", label: "Phase 2 — Design" },
  { id: "execute", label: "Phase 3 — Execute" },
  { id: "outcomes", label: "Outcomes" },
  { id: "deliverables", label: "Deliverables" },
];

const diagnosticModules = [
  {
    id: "D1",
    name: "Revenue Motion Audit",
    what: "A structured assessment of your current GTM motion — how pipeline is generated, where it breaks down, and what's driving or killing conversion.",
    how: [
      "1:1 interviews with sales, marketing, and leadership (30-60 min each)",
      "Pipeline data review — stage-by-stage conversion, velocity, and source analysis",
      "Messaging audit across website, email, and sales collateral",
      "Competitive positioning review",
    ],
    outputs: [
      "Revenue Motion Audit Report (3-5 pages)",
      "Pipeline breakdown map — where deals stall and why",
      "Conversion rate baseline by stage",
      "Top 3 priority gaps ranked by revenue impact",
    ],
    signal: "You'll know exactly where your GTM is leaking — not a gut feeling, a documented diagnosis.",
  },
  {
    id: "D2",
    name: "ICP Validation & Segmentation",
    what: "Most companies think they know their ICP. Few have validated it with data. This module stress-tests your assumptions against actual win/loss patterns and market signals.",
    how: [
      "Win/loss analysis across last 12–24 months of deals",
      "Customer interview series (3-5 interviews with best-fit customers)",
      "Firmographic and technographic segmentation of your best accounts",
      "Buy signal mapping — what triggers a purchase decision",
    ],
    outputs: [
      "Validated ICP document — 2–4 primary segments with full profiles",
      "Win/loss analysis summary",
      "Buy signal framework — pain points, triggers, timing, and desired customer outcomes",
      "ICP scoring model for sales qualification",
    ],
    signal: "Your sales team stops chasing bad-fit deals. Pipeline quality improves before volume does.",
  },
  {
    id: "D3",
    name: "Competitive & Market Positioning",
    what: "Where do you actually win, and why? This module maps your positioning against the competitive landscape and identifies the whitespace where you have the strongest right to win.",
    how: [
      "Competitive landscape mapping (direct, indirect, status quo)",
      "Positioning matrix — where you win, lose, and why",
      "Message differentiation analysis",
      "Category and narrative assessment",
    ],
    outputs: [
      "Competitive landscape map",
      "Positioning statement and message architecture",
      "Differentiation framework — what you say and what you don't",
      "Category narrative (if applicable)",
    ],
    signal: "Your team can articulate why you win in one sentence. Every prospect-facing asset reflects it.",
  },
];

const architectureModules = [
  {
    id: "A1",
    name: "GTM Motion Design",
    what: "The full architecture of how you go to market — channels, sequencing, and the coordinated motion from awareness to closed revenue.",
    deliverables: [
      "Channel strategy and prioritization",
      "Campaign architecture — awareness, conversion, retention, and growth",
      "Account-based motion design (if applicable)",
      "Lead scoring and qualification framework",
      "Handoff protocols for seamless customer journey. From MQL to Closed Won",
    ],
    outcome: "A documented, repeatable GTM motion that doesn't depend on any single person.",
  },
  {
    id: "A2",
    name: "Sales Playbook",
    what: "A complete, stage-by-stage playbook your team can execute from day one — built from your actual ICP and win patterns, not a generic template.",
    deliverables: [
      "Stage-by-stage sales process with entry and exit criteria",
      "Discovery framework — questions, listening guides, qualification criteria",
      "Objection handling library (built from real win/loss data)",
      "Proposal and pricing guidance",
      "Follow-up and nurture sequences by deal stage",
      "Talk tracks for each ICP segment",
    ],
    outcome: "New reps ramp faster. Existing reps close more consistently. Pipeline velocity improves.",
  },
  {
    id: "A3",
    name: "Messaging & Content",
    what: "The full messaging system — from positioning statement to email subject lines — built on the validated ICP and competitive positioning from Phase 1.",
    deliverables: [
      "Core positioning statement and narrative",
      "Message matrix by ICP segment and buyer persona",
      "Email sequence library (cold outreach, nurture, re-engagement)",
      "LinkedIn content framework and post templates",
      "Website copy recommendations",
      "Sales deck structure and narrative flow",
    ],
    outcome: "Consistent messaging across every channel. Prospects hear the same story everywhere.",
  },
  {
    id: "A4",
    name: "Revenue Infrastructure",
    what: "The operational layer that makes the motion repeatable — measurement systems, toolstack recommendations, and the reporting cadence that keeps everything accountable.",
    deliverables: [
      "KPI framework and dashboard design",
      "CRM audit and configuration recommendations",
      "Pipeline review cadence and meeting structure",
      "Toolstack assessment and gap analysis",
      "RevOps process documentation",
    ],
    outcome: "GTM becomes measurable, manageable, and improvable — not just executable.",
  },
];

const activationModules = [
  {
    id: "AC1",
    name: "Launch & Enablement",
    desc: "The playbook gets activated — not just handed over. Includes team enablement sessions, launch sequencing, and a 30-day activation plan with clear owners and checkpoints.",
    milestone: "Team is running the new motion within 30 days of delivery.",
  },
  {
    id: "AC2",
    name: "Campaign Execution Support",
    desc: "First campaign launches with Cambrian Catalyst alongside — reviewing copy, coaching on discovery calls, and iterating based on early signals. This is where the playbook meets reality.",
    milestone: "First ICP-qualified pipeline generated within 45 days of activation.",
  },
  {
    id: "AC3",
    name: "Measurement & Iteration",
    desc: "At 30, 60, and 90 days, a formal review of what's working and what needs adjustment. The playbook is a living document — it improves with data.",
    milestone: "Measurable improvement in at least 2 pipeline KPIs within 90 days.",
  },
];

const outcomeMetrics = [
  {
    category: "Pipeline Quality",
    before: "Deals represent a mix of good-fit and bad-fit accounts. Conversion rates are inconsistent and hard to predict.",
    after: "Pipeline is ICP-qualified from first touch. Conversion rates stabilize and improve. Sales cycles shorten.",
    metrics: [
      { label: "ICP-qualified pipeline %", benchmark: "Target: 70%+ of pipeline from validated ICP segments" },
      { label: "Stage-to-stage conversion", benchmark: "Baseline set in Phase 1; target 15–25% improvement by 90 days" },
      { label: "Average sales cycle length", benchmark: "Target reduction of 20%+ from baseline" },
    ],
  },
  {
    category: "Outbound Effectiveness",
    before: "Outreach is inconsistent, response rates are low, and there's no clear signal on what's working.",
    after: "Targeted outreach to validated ICP contacts with tested messaging. Response rates improve. Outbound pipeline becomes predictable.",
    metrics: [
      { label: "Cold email reply rate", benchmark: "Industry avg: 3–5%. Target with ICP list + tested messaging: 8–15%" },
      { label: "Meeting conversion rate", benchmark: "Target: 25–40% of positive replies convert to discovery call" },
      { label: "Outbound pipeline contribution", benchmark: "Target: 40%+ of new pipeline from outbound motion" },
    ],
  },
  {
    category: "Revenue Velocity",
    before: "Revenue growth is unpredictable. Forecasting is guesswork. Wins feel random.",
    after: "A documented motion creates predictable pipeline. Forecasting improves. Growth becomes plannable.",
    metrics: [
      { label: "Pipeline coverage ratio", benchmark: "Target: 3–4x quarterly revenue target in qualified pipeline" },
      { label: "Win rate", benchmark: "Baseline set in Phase 1; target 20%+ improvement within 6 months" },
      { label: "Revenue from new ICP segments", benchmark: "New segments contributing within 90 days of activation" },
    ],
  },
  {
    category: "Team Performance",
    before: "GTM depends on a few key people. Knowledge isn't documented. New hires take 6+ months to ramp.",
    after: "Playbooks and processes are documented. New reps ramp faster. Performance is consistent across the team.",
    metrics: [
      { label: "New rep ramp time", benchmark: "Target: 50% reduction in time to first qualified opportunity" },
      { label: "Rep performance variance", benchmark: "Target: 30% tightening of spread across team" },
      { label: "Forecast accuracy", benchmark: "Target: within 15% of monthly forecast by month 3" },
    ],
  },
];

const allDeliverables = [
  {
    phase: "Phase 1 — Diagnose",
    items: [
      "Revenue Motion Audit Report (3-5 pages)",
      "Pipeline breakdown map",
      "Validated ICP document (2–4 segments)",
      "Win/loss analysis summary",
      "Buy signal framework",
      "ICP scoring model",
      "Competitive landscape map",
      "Positioning statement and message architecture",
    ],
  },
  {
    phase: "Phase 2 — Design",
    items: [
      "GTM Motion Design document",
      "Campaign architecture (awareness, conversion, retention)",
      "Full Sales Playbook",
      "Discovery and qualification framework",
      "Objection handling library",
      "Email sequence library",
      "LinkedIn content framework",
      "Message matrix by ICP and persona",
      "KPI framework and dashboard design",
      "CRM audit and configuration recommendations",
      "RevOps process documentation",
    ],
  },
  {
    phase: "Phase 3 — Execute",
    items: [
      "30-day activation plan with owners and checkpoints",
      "Team enablement session(s)",
      "Campaign launch support",
      "30/60/90-day review reports",
      "Playbook iteration based on live data",
    ],
  },
];

export default function App() {
  const [active, setActive] = useState("overview");
  const [expanded, setExpanded] = useState(null);
  const toggle = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: "#ffffff", minHeight: "100vh", color: "#1a1c1a" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;1,400&family=Lora:ital,wght@0,600;1,400&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .nav-btn { background: none; border: none; cursor: pointer; font-family: inherit; }
        .expandable { cursor: pointer; }
        .expandable:hover { background: #fafaf8 !important; }
        .fade { animation: fadeIn 0.2s ease; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 620px) {
          .two-col { grid-template-columns: 1fr !important; }
          .three-col { grid-template-columns: 1fr !important; }
          .nav-scroll { overflow-x: auto; }
        }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom: "1px solid #e8e8e5", padding: "36px 40px 0" }}>
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <p style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#aaa", marginBottom: 12, fontWeight: 500 }}>
            Cambrian Catalyst · Client Engagement Framework Example
          </p>
          <h1 style={{ fontFamily: "'Lora', Georgia, serif", fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 600, color: "#1a1c1a", marginBottom: 32, lineHeight: 1.2, letterSpacing: "-0.01em" }}>
            Revenue Excellence & GTM
          </h1>
          <div className="nav-scroll" style={{ display: "flex", borderTop: "1px solid #e8e8e5" }}>
            {sections.map(s => (
              <button key={s.id} className="nav-btn" onClick={() => { setActive(s.id); setExpanded(null); }}
                style={{
                  padding: "14px 16px",
                  fontSize: 16,
                  color: active === s.id ? "#1a1c1a" : "#aaa",
                  borderBottom: active === s.id ? "2px solid #1a1c1a" : "2px solid transparent",
                  fontWeight: active === s.id ? 500 : 400,
                  whiteSpace: "nowrap",
                  letterSpacing: "0.01em",
                  transition: "color 0.15s",
                }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "48px 40px 100px" }} className="fade" key={active}>

        {/* OVERVIEW */}
        {active === "overview" && (
          <div>
            <SectionHeader
              title="What's Working and What Needs Help?"
              sub="A structured engagement that maps your current revenue motion, identifies the highest-impact gaps, and builds the GTM infrastructure to close them — with measurable outcomes at every phase."
            />
            <blockquote style={{ borderLeft: "2px solid #1a1c1a", paddingLeft: 20, margin: "0 0 52px", fontFamily: "'Lora', Georgia, serif", fontStyle: "italic", fontSize: 16, color: "#555", lineHeight: 1.9 }}>
              Most GTM engagements start with a framework and apply it. We start with a diagnosis — because the right motion for your business depends entirely on where you are, who you're selling to, and what's actually happening in your pipeline today. We build nothing until we understand everything.
            </blockquote>

            <Label>The engagement</Label>
            <div className="three-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, background: "#e8e8e5", border: "1px solid #e8e8e5", marginBottom: 52 }}>
              {[
                { num: "01", label: "Diagnose", sub: "Weeks 1–3", desc: "Assess the current state — pipeline, ICP, positioning, competitive landscape. No assumptions, no generic frameworks." },
                { num: "02", label: "Design", sub: "Weeks 4–8", desc: "Build the motion — playbooks, messaging, campaign architecture, and revenue infrastructure tailored to your specific context." },
                { num: "03", label: "Execute", sub: "Weeks 9–16", desc: "Launch with Cambrian Catalyst alongside for the first campaign cycle. Measure, iterate, and hand off a self-sustaining motion." },
              ].map((p, i) => (
                <div key={i} style={{ background: "#fff", padding: "28px 24px" }}>
                  <div style={{ fontSize: 11, color: "#ccc", letterSpacing: "0.1em", marginBottom: 14, fontWeight: 500 }}>{p.num} · {p.sub}</div>
                  <div style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 17, fontWeight: 600, color: "#1a1c1a", marginBottom: 10 }}>{p.label}</div>
                  <div style={{ fontSize: 13, color: "#777", lineHeight: 1.75 }}>{p.desc}</div>
                </div>
              ))}
            </div>

            <Label>What makes this different</Label>
            <div style={{ borderTop: "1px solid #e8e8e5" }}>
              {[
                { title: "Diagnosis before prescription", body: "We audit your actual pipeline data, win/loss patterns, and customer conversations before recommending anything. The engagement is customized to what we find." },
                { title: "Operator-built, not consultant-templated", body: "Every deliverable is built from your specific ICP, competitive position, and team reality — not adapted from a generic playbook. The frameworks come from time spent in the seat." },
                { title: "Measurable at every phase", body: "KPIs are established in Phase 1 using your own baseline data. Every outcome is tracked against that baseline — not industry averages alone." },
                { title: "Built to run without us", body: "The goal is a self-sustaining GTM motion. Documentation, enablement, and process design are built for your team to own when the engagement ends." },
              ].map((d, i) => (
                <div key={i} style={{ display: "flex", gap: 40, padding: "24px 0", borderBottom: "1px solid #e8e8e5" }}>
                  <div style={{ width: 200, flexShrink: 0, fontSize: 13, fontWeight: 500, color: "#1a1c1a", lineHeight: 1.5, paddingTop: 2 }}>{d.title}</div>
                  <div style={{ fontSize: 13, color: "#777", lineHeight: 1.8 }}>{d.body}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DIAGNOSE */}
        {active === "diagnose" && (
          <div>
            <SectionHeader title="Phase 1 — Diagnose" sub="We assess before we build. Three diagnostic modules run in parallel across weeks 1–3, producing a complete picture of your current revenue motion and the highest-impact gaps to address." />
            <PhaseStrip label="Weeks 1–3" steps={["Kickoff & data access", "Stakeholder interviews", "Pipeline analysis", "Customer interviews", "Audit report"]} />
            <div style={{ borderTop: "1px solid #e8e8e5" }}>
              {diagnosticModules.map((m, i) => (
                <div key={i} className="expandable" onClick={() => toggle(m.id)}
                  style={{ borderBottom: "1px solid #e8e8e5", background: expanded === m.id ? "#fafaf8" : "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "26px 0", gap: 24 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 14, alignItems: "baseline", marginBottom: 8 }}>
                        <span style={{ fontSize: 11, color: "#ccc", fontWeight: 500 }}>{m.id}</span>
                        <span style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 17, fontWeight: 600, color: "#1a1c1a" }}>{m.name}</span>
                      </div>
                      <p style={{ fontSize: 13, color: "#777", lineHeight: 1.75, maxWidth: 560 }}>{m.what}</p>
                    </div>
                    <span style={{ fontSize: 18, color: "#ccc", flexShrink: 0, paddingTop: 2, fontWeight: 300 }}>{expanded === m.id ? "−" : "+"}</span>
                  </div>
                  {expanded === m.id && (
                    <div className="fade" style={{ paddingBottom: 28 }}>
                      <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
                        <div>
                          <Label small>How we do it</Label>
                          {m.how.map((h, j) => <BulletItem key={j} text={h} />)}
                        </div>
                        <div>
                          <Label small>What you receive</Label>
                          {m.outputs.map((o, j) => <BulletItem key={j} text={o} />)}
                          <div style={{ marginTop: 20, padding: "14px 16px", background: "#f7f7f5", borderLeft: "2px solid #1a1c1a" }}>
                            <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#aaa", marginBottom: 6, fontWeight: 500 }}>Signal of success</div>
                            <div style={{ fontSize: 13, color: "#555", lineHeight: 1.7, fontStyle: "italic" }}>{m.signal}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DESIGN */}
        {active === "design" && (
          <div>
            <SectionHeader title="Phase 2 — Design" sub="With the diagnostic complete, we build the full GTM motion — tailored to your validated ICP, competitive position, and team reality. Four modules delivered across weeks 4–8." />
            <PhaseStrip label="Weeks 4–8" steps={["Diagnostic synthesis", "Motion design", "Playbook build", "Messaging system", "Revenue infrastructure"]} />
            <div style={{ borderTop: "1px solid #e8e8e5" }}>
              {architectureModules.map((m, i) => (
                <div key={i} className="expandable" onClick={() => toggle(m.id)}
                  style={{ borderBottom: "1px solid #e8e8e5", background: expanded === m.id ? "#fafaf8" : "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "26px 0", gap: 24 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 14, alignItems: "baseline", marginBottom: 8 }}>
                        <span style={{ fontSize: 11, color: "#ccc", fontWeight: 500 }}>{m.id}</span>
                        <span style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 17, fontWeight: 600, color: "#1a1c1a" }}>{m.name}</span>
                      </div>
                      <p style={{ fontSize: 13, color: "#777", lineHeight: 1.75, maxWidth: 560 }}>{m.what}</p>
                    </div>
                    <span style={{ fontSize: 18, color: "#ccc", flexShrink: 0, paddingTop: 2, fontWeight: 300 }}>{expanded === m.id ? "−" : "+"}</span>
                  </div>
                  {expanded === m.id && (
                    <div className="fade" style={{ paddingBottom: 28 }}>
                      <Label small>Deliverables</Label>
                      <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 48px", marginBottom: 20 }}>
                        {m.deliverables.map((d, j) => <BulletItem key={j} text={d} />)}
                      </div>
                      <div style={{ padding: "14px 16px", background: "#f7f7f5", borderLeft: "2px solid #1a1c1a", display: "inline-block" }}>
                        <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#aaa", marginBottom: 6, fontWeight: 500 }}>Outcome</div>
                        <div style={{ fontSize: 13, color: "#555", lineHeight: 1.7, fontStyle: "italic" }}>{m.outcome}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EXECUTE */}
        {active === "execute" && (
          <div>
            <SectionHeader title="Phase 3 — Execute" sub="The playbook launches — with Cambrian Catalyst alongside for the first campaign cycle. This is where documentation becomes motion and motion becomes pipeline." />
            <PhaseStrip label="Weeks 9–16" steps={["Team enablement", "Campaign launch", "Pipeline review", "30-day assessment", "60-day iteration", "90-day report"]} />
            <div style={{ borderTop: "1px solid #e8e8e5" }}>
              {activationModules.map((m, i) => (
                <div key={i} style={{ borderBottom: "1px solid #e8e8e5", padding: "28px 0" }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "baseline", marginBottom: 10 }}>
                    <span style={{ fontSize: 11, color: "#ccc", fontWeight: 500 }}>{m.id}</span>
                    <span style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 17, fontWeight: 600, color: "#1a1c1a" }}>{m.name}</span>
                  </div>
                  <p style={{ fontSize: 13, color: "#777", lineHeight: 1.8, maxWidth: 580, marginBottom: 16 }}>{m.desc}</p>
                  <div style={{ display: "inline-flex", gap: 12, alignItems: "baseline", padding: "10px 16px", background: "#f7f7f5" }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#aaa", fontWeight: 500 }}>Milestone</span>
                    <span style={{ fontSize: 13, color: "#1a1c1a" }}>{m.milestone}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 52 }}>
              <Label>Review cadence</Label>
              <div className="three-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, background: "#e8e8e5", border: "1px solid #e8e8e5" }}>
                {[
                  { label: "Day 30", body: "Progress snapshot — what's been executed, early signals, first adjustments." },
                  { label: "Day 60", body: "Mid-point review — pipeline contribution, conversion rate movement, playbook iteration." },
                  { label: "Day 90", body: "Outcomes report — full KPI review against Phase 1 baseline, next phase recommendations." },
                ].map((r, i) => (
                  <div key={i} style={{ background: "#fff", padding: "24px 22px" }}>
                    <div style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 15, fontWeight: 600, color: "#1a1c1a", marginBottom: 10 }}>{r.label}</div>
                    <div style={{ fontSize: 13, color: "#777", lineHeight: 1.75 }}>{r.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* OUTCOMES */}
        {active === "outcomes" && (
          <div>
            <SectionHeader title="Outcomes" sub="KPIs are established during Phase 1 using your actual pipeline data as the baseline. Every metric below is tracked against that baseline — not against industry averages alone." />
            <div style={{ borderTop: "1px solid #e8e8e5" }}>
              {outcomeMetrics.map((o, i) => (
                <div key={i} className="expandable" onClick={() => toggle(`o${i}`)}
                  style={{ borderBottom: "1px solid #e8e8e5", background: expanded === `o${i}` ? "#fafaf8" : "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "22px 0", gap: 24 }}>
                    <span style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 17, fontWeight: 600, color: "#1a1c1a" }}>{o.category}</span>
                    <span style={{ fontSize: 18, color: "#ccc", flexShrink: 0, fontWeight: 300 }}>{expanded === `o${i}` ? "−" : "+"}</span>
                  </div>
                  {expanded === `o${i}` && (
                    <div className="fade" style={{ paddingBottom: 28 }}>
                      <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
                        <div style={{ padding: "16px 18px", background: "#fdf6f6", borderLeft: "2px solid #d8a8a8" }}>
                          <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#c08080", marginBottom: 8, fontWeight: 500 }}>Before</div>
                          <div style={{ fontSize: 13, color: "#666", lineHeight: 1.75 }}>{o.before}</div>
                        </div>
                        <div style={{ padding: "16px 18px", background: "#f7f7f5", borderLeft: "2px solid #1a1c1a" }}>
                          <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#aaa", marginBottom: 8, fontWeight: 500 }}>After</div>
                          <div style={{ fontSize: 13, color: "#444", lineHeight: 1.75 }}>{o.after}</div>
                        </div>
                      </div>
                      <Label small>Tracked metrics & benchmarks</Label>
                      {o.metrics.map((m, j) => (
                        <div key={j} style={{ display: "flex", gap: 32, padding: "13px 0", borderBottom: "1px solid #f0f0ee" }}>
                          <div style={{ width: 210, flexShrink: 0, fontSize: 13, fontWeight: 500, color: "#1a1c1a", lineHeight: 1.5 }}>{m.label}</div>
                          <div style={{ fontSize: 13, color: "#777", lineHeight: 1.6 }}>{m.benchmark}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DELIVERABLES */}
        {active === "deliverables" && (
          <div>
            <SectionHeader title="Deliverables" sub="Every document, framework, and artifact produced across the engagement — built specifically for your business, not adapted from templates." />
            {allDeliverables.map((phase, i) => (
              <div key={i} style={{ marginBottom: 44 }}>
                <Label>{phase.phase}</Label>
                <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 48px", borderTop: "1px solid #e8e8e5" }}>
                  {phase.items.map((item, j) => (
                    <div key={j} style={{ fontSize: 13, color: "#555", padding: "12px 0", borderBottom: "1px solid #f0f0ee", lineHeight: 1.5 }}>{item}</div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ padding: "20px 24px", background: "#f7f7f5", borderLeft: "2px solid #1a1c1a" }}>
              <p style={{ fontSize: 13, color: "#666", lineHeight: 1.8, fontStyle: "italic" }}>
                Actual Deliverables may vary. All agred upon deliverables are yours to own and operate. The engagement is designed so your team can run the motion independently — Cambrian Catalyst is not a dependency, it's an enabler.
              </p>
            </div>
          </div>
        )}

      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #e8e8e5", padding: "20px 40px", textAlign: "center" }}>
        <a href="https://cambriacatalyst.com" style={{ fontSize: 11, color: "#ccc", textDecoration: "none", letterSpacing: "0.12em", textTransform: "uppercase" }}>
          cambriacatalyst.com
            © 2026 Cambrian Catalyst LLC · Seattle, WA · All rights reserved
        </a>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: 44 }}>
      <h2 style={{ fontFamily: "'Lora', Georgia, serif", fontSize: "clamp(20px, 2.5vw, 26px)", fontWeight: 600, color: "#1a1c1a", marginBottom: 12, letterSpacing: "-0.01em", lineHeight: 1.3 }}>{title}</h2>
      <p style={{ fontSize: 14, color: "#888", lineHeight: 1.8, maxWidth: 580 }}>{sub}</p>
    </div>
  );
}

function Label({ children, small }) {
  return (
    <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "#aaa", fontWeight: 500, marginBottom: small ? 12 : 18 }}>
      {children}
    </div>
  );
}

function BulletItem({ text }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "8px 0", fontSize: 13, color: "#666", lineHeight: 1.6, borderBottom: "1px solid #f0f0ee" }}>
      <span style={{ color: "#ddd", flexShrink: 0 }}>—</span>
      <span>{text}</span>
    </div>
  );
}

function PhaseStrip({ label, steps }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 0", marginBottom: 28, borderBottom: "1px solid #e8e8e5", flexWrap: "wrap" }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: "#bbb", letterSpacing: "0.06em", marginRight: 4 }}>{label}</span>
      {steps.map((s, i) => (
        <span key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#bbb" }}>{s}</span>
          {i < steps.length - 1 && <span style={{ color: "#ddd", fontSize: 10 }}>→</span>}
        </span>
      ))}
    </div>
  );
}
